import { useEffect, useSyncExternalStore } from 'react';

import { RELEASE_NOTES, type ReleaseNotesEntry } from './manifest';
import { compareSemver } from './semver';

/** Newest release-notes version this browser has opened (bare semver). */
export const SEEN_KEY = 'stitchpress-whats-new-seen';

/** The seam: today localStorage, later possibly the backend user settings.
 *  Swapping the implementation must not touch any caller. */
export interface SeenStore {
    read(): string | null;
    write(version: string): void;
    subscribe(cb: () => void): () => void;
}

/** localStorage-backed store. Reads/writes never throw: blocked storage
 *  (private window, cleared site data) reads as null and drops writes. Uses
 *  the bare `localStorage` global so tests can swap it. */
export function createLocalSeenStore(key: string = SEEN_KEY): SeenStore {
    const listeners = new Set<() => void>();
    const notify = () => {
        for (const cb of listeners) cb();
    };
    const onStorage = (e: StorageEvent) => {
        if (e.key === key || e.key === null) notify();
    };
    return {
        read() {
            try {
                return localStorage.getItem(key);
            } catch {
                return null;
            }
        },
        write(version) {
            try {
                localStorage.setItem(key, version);
            } catch {
                return;
            }
            notify();
        },
        subscribe(cb) {
            listeners.add(cb);
            if (listeners.size === 1) window.addEventListener('storage', onStorage);
            return () => {
                listeners.delete(cb);
                if (listeners.size === 0) window.removeEventListener('storage', onStorage);
            };
        },
    };
}

export const whatsNewSeenStore: SeenStore = createLocalSeenStore();

/** Unread = there are notes and they're newer than what was opened. Nothing
 *  stored (a first run) is never unread — see useWhatsNewUnread's seeding. */
export function isUnread(latest: string | undefined, seen: string | null): boolean {
    return latest != null && seen != null && compareSemver(latest, seen) > 0;
}

/** Whether to show the What's-new dot. A first run (nothing stored) is seeded
 *  with the newest version in an effect — never during render — so visitors
 *  aren't shown a dot for notes that aren't new to them. */
export function useWhatsNewUnread(
    entries: readonly ReleaseNotesEntry[] = RELEASE_NOTES,
    store: SeenStore = whatsNewSeenStore,
): boolean {
    const seen = useSyncExternalStore(store.subscribe, store.read, () => null);
    const latest = entries[0]?.version;
    useEffect(() => {
        if (latest != null && seen == null) store.write(latest);
    }, [latest, seen, store]);
    return isUnread(latest, seen);
}

/** Record the newest notes as seen (the modal opened, or /whats-new mounted). */
export function markLatestSeen(
    entries: readonly ReleaseNotesEntry[] = RELEASE_NOTES,
    store: SeenStore = whatsNewSeenStore,
): void {
    const latest = entries[0]?.version;
    if (latest != null) store.write(latest);
}
